import { ApiError, errorKeyForSuiteApiResult } from './errors';
import { PortalClient } from './portal-client';
import type {
  AddressDto,
  AddressRequest,
  OrganizationAddRequest,
  OrganizationAddResult,
  OrganizationSettings,
  OrganizationUpdateRequest,
} from './types';
import { ESuiteApiResult } from './types';

/**
 * Organization API — thin, typed wrapper over the Portal's authenticated organization endpoint:
 *
 *   PUT SuiteAPI/Organization/AddV1
 *   Headers: brandID + Authorization: Bearer <jwt> (JWT from ConfirmEmailV1).
 *   Body (OrganizationAddRequest): company info + address.
 *   Response: SuiteApiResult<Organization>.
 *
 * See Server/Portal/Areas/SuiteAPI/Controllers/OrganizationController.cs for the Portal-side implementation.
 */
export class OrganizationApi {
  private readonly client: PortalClient;

  public constructor(client: PortalClient) {
    this.client = client;
  }

  /**
   * Creates the organization for the authenticated user (registration step 2).
   *
   * @throws {ApiError} with a translatable key on failure or when the server
   * response does not contain the created organization.
   */
  public async add(request: OrganizationAddRequest, signal?: AbortSignal): Promise<OrganizationAddResult> {
    const result = await this.client.request<OrganizationAddResult>({
      method: 'PUT',
      path: '/SuiteAPI/Organization/AddV1',
      body: request,
      authenticated: true,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    if (!result.data?.id) {
      throw new ApiError('server', 'invalidResponse', { serverResult });
    }

    return result.data;
  }

  /**
   * Fetches the authenticated user's organization settings (Settings page — organization section).
   *
   * @throws {ApiError} with a translatable key on failure.
   */
  public async get(signal?: AbortSignal): Promise<OrganizationSettings> {
    const result = await this.client.request<OrganizationSettings>({
      method: 'GET',
      path: '/SuiteAPI/Organization/GetV1',
      authenticated: true,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;
    if (serverResult !== ESuiteApiResult.Success || !result.data) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }
    return result.data;
  }

  /**
   * Updates the authenticated user's organization settings (Settings page — organization section).
   *
   * @throws {ApiError} with a translatable key on failure.
   */
  public async update(
    request: OrganizationUpdateRequest,
    signal?: AbortSignal,
  ): Promise<OrganizationSettings> {
    const result = await this.client.request<OrganizationSettings>({
      method: 'PUT',
      path: '/SuiteAPI/Organization/UpdateV1',
      body: request,
      authenticated: true,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;
    if (serverResult !== ESuiteApiResult.Success || !result.data) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }
    return result.data;
  }

  /**
   * Fetches the authenticated user's organization's addresses.
   *
   * @throws {ApiError} with a translatable key on failure.
   */
  public async getAddresses(signal?: AbortSignal): Promise<AddressDto[]> {
    const result = await this.client.request<AddressDto[]>({
      method: 'GET',
      path: '/SuiteAPI/Organization/AddressesV1',
      authenticated: true,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;
    if (serverResult !== ESuiteApiResult.Success || !result.data) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }
    return result.data;
  }

  /**
   * Adds an address to the authenticated user's organization.
   *
   * @throws {ApiError} with a translatable key on failure.
   */
  public async addAddress(request: AddressRequest, signal?: AbortSignal): Promise<AddressDto> {
    const result = await this.client.request<AddressDto>({
      method: 'PUT',
      path: '/SuiteAPI/Organization/AddressAddV1',
      body: request,
      authenticated: true,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;
    if (serverResult !== ESuiteApiResult.Success || !result.data) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }
    return result.data;
  }

  /**
   * Deletes an address from the authenticated user's organization by ID.
   *
   * @throws {ApiError} with a translatable key on failure.
   */
  public async deleteAddress(id: string, signal?: AbortSignal): Promise<void> {
    const result = await this.client.request<boolean>({
      method: 'PUT',
      path: '/SuiteAPI/Organization/AddressDeleteV1',
      body: id,
      authenticated: true,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;
    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }
  }
}
